<div
  class="relative h-full w-full"
  *ngIf="!isLoading; else metricCardSkeletonLoader"
>
  <div
    [id]="serviceMetricId + '-service-metric-card'"
    class="service-metric-tile h-full w-full box-border p-4 rounded-lg border-solid border-1 border-sky-blue"
  >
    <div class="flex flex-col gap-4 h-full w-full justify-between">
      <section class="metric-card-body flex flex-col gap-2">
        <section class="metric-summary flex flex-col gap-1">
          <!-- Metric title and add to presentation checkbox-->
          <section
            class="metric-title flex flex-col lg:flex-row justify-between items-start"
          >
            <span class="metric-card-title text-base font-medium">{{
              metricTitle
            }}</span>
            <div
              class="checkbox-container flex gap-2 items-center"
              *ngIf="isEnableCheckBox"
            >
              <input
                class="metric-card-checkbox w-4 h-4 accent-blue"
                #addServiceMetric
                type="checkbox"
                [checked]="checked"
                (click)="$event.stopPropagation()"
                (change)="onCheckedChange(addServiceMetric.checked)"
              />
              <span class="text-sm font-normal">Add</span>
            </div>
          </section>

          <!-- Metric count and growth. Different layouts and elements for Pest vs Non Pest cards -->
          <ng-container
            *ngIf="division == Division.Pest; else nonPestServiceMetricBlock"
          >
            <ng-container
              *ngTemplateOutlet="pestServiceMetricBlock"
            ></ng-container>
          </ng-container>
        </section>

        <section
          class="metric-average flex items-center justify-between gap-3 text-shuttle-gray"
        >
          <span>
            <span class="font-medium">{{ this.avgPerLocation }}</span> avg per Location <span *ngIf="division == Division.Pest">Serviced</span>
          </span>

          <div
            *ngIf="metricKey === SICallTypes.TrainingSessions && trainingBreakdown"
            class="ml-auto inline-flex shrink-0 items-center gap-1 text-sm font-medium text-blue cursor-pointer"
            (click)="$event.stopPropagation(); onClickTrainingBreakdown()"
          >
            {{ isBreakdownVisible ? 'Hide' : 'Details' }}
            <img
              src="assets/shared-icons/neptune-icon-blue-chevron-right.svg"
              class="!h-4 !w-4 block transition-transform duration-200 ease-out"
              [class.rotate-180]="isBreakdownVisible"
              alt="toggle-training-breakdown-icon"
            />
          </div>
        </section>
      </section>

      <!-- Details action -->
      <section class="metric-card-footer" *ngIf="division != Division.Pest">
        <div
          class="view-details-button neptune-button-white text-sm flex justify-center items-center px-3 py-2 cursor-pointer box-border"
          (click)="onClickViewDetails()"
        >
          View Details
          <img
            src="assets/shared-icons/neptune-icon-blue-chevron-right.svg"
            class="!h-5 !w-5 block"
            alt="arrow-right-icon"
          />
        </div>
      </section>
    </div>
  </div>

  <!-- Training breakdown section -->
  <section
    *ngIf="metricKey === SICallTypes.TrainingSessions && trainingBreakdown"
    class="training-breakdown-section absolute left-full top-[4.2rem] z-20 ml-[0.7rem] w-[min(53rem,calc(100vw-3rem))] origin-top-left rounded-lg bg-white p-4 shadow-neptune-elevation-2 transition-all duration-200 ease-out"
    [class.opacity-0]="!isBreakdownVisible"
    [class.scale-95]="!isBreakdownVisible"
    [class.pointer-events-none]="!isBreakdownVisible"
    [class.opacity-100]="isBreakdownVisible"
    [class.scale-100]="isBreakdownVisible"
    [attr.aria-hidden]="!isBreakdownVisible"
    appClickOutside
    (appClickOutside)="onClickOutsideTrainingBreakdown()"
    aria-label="Training Breakdown"
  >
    <!-- Arrow indicators for the training breakdown section -->
    <span class="pointer-events-none absolute -left-[9px] top-[19px] h-0 w-0 border-solid border-l-0 border-y-[9px] border-r-[9px] border-y-transparent border-r-sky-blue"></span>
    <span class="pointer-events-none absolute -left-2 top-5 h-0 w-0 border-solid border-l-0 border-y-8 border-r-8 border-y-transparent border-r-white"></span>

    <div class="grid grid-cols-1 items-start gap-4 sm:grid-cols-2 sm:items-start lg:grid-cols-4 breakdown-container">
      <ng-container *ngFor="let item of trainingBreakdownColumns; let last = last; trackBy: trackByColumnTitle">
        <div class="flex flex-col gap-1 breakdown-group" [ngClass]="!last ? 'border-0 border-r-1 border-solid border-sky-blue pr-4' : ''">
          <div class="text-base font-medium text-neptune-neutral">{{ item.title }}</div>
          <div class="mt-2 flex items-center gap-2">
            <span class="count-number text-[28px] leading-9 font-medium text-neptune-default">{{ item.value.total }}</span>
            <span class="inline-flex items-center text-sm font-medium text-neptune-color-primary-500">
              <img
                class="w-5 h-5 block"
                src="/assets/shared-icons/neptune-icon-blue-solid-chevron-down.svg"
                alt="growth-arrow-icon"
                [ngClass]="(item.value.percentageChange ?? 0) >= 0 ? 'rotate-180' : ''"
              />
              <span class="percent-change">{{ (item.value.percentageChange ?? 0) | signedNumber }}%</span>
            </span>
          </div>
          <div class="mt-1 text-sm text-shuttle-gray">
            <span class="avg-value font-medium">{{ item.value.avgPerLocation ?? 0 }}</span> avg per Location
          </div>
          <div class="description mt-1 text-xs text-shuttle-gray">{{ item.description }}</div>
        </div>
      </ng-container>
    </div>
  </section>
</div>

<ng-template #pestServiceMetricBlock>
  <section class="metric-value flex gap-2 items-center justify-between">
    <div class="h-full flex flex-col gap-2">
      <span class="time-period text-xs text-neptune-neutral font-medium">{{
        periodRange | monthYearRange
      }}</span>
      <div class="metric-count text-3xl">
        {{ this.totalMetricCount | number: "1.00" : "en-US" }}
      </div>
    </div>

    <div class="h-full flex flex-col gap-2">
      <span class="comparison-type-indicator text-xs text-neptune-neutral font-medium">YoY Change</span>
      <div
        class="growth-block flex items-center"
        *ngIf="growthDateRangeAvailable && this.percentGrowth; else noGrowthData"
      >
        <span class="arrow flex items-center">
          <img
            class="w-5 h-5 block"
            src="/assets/shared-icons/neptune-icon-blue-solid-chevron-down.svg"
            alt="growth-arrow-icon"
            [ngClass]="percentGrowth >= 0 ? 'rotate-180' : ''"
          />
        </span>
        <span class="growth-value text-neptune-color-primary-500 font-medium">
          {{ this.percentGrowth | signedNumber }}%
        </span>
      </div>
      <ng-template #noGrowthData>
        <span class="growth-value text-neptune-color-primary-500 font-medium">N/A</span>
      </ng-template>
    </div>
  </section>
</ng-template>

<ng-template #nonPestServiceMetricBlock>
  <section class="metric-value flex gap-2 items-center justify-start">
    <div class="metric-count text-3xl">
      {{ this.totalMetricCount | number: "1.00" : "en-US" }}
    </div>

    <div
      class="growth-block flex items-center"
      *ngIf="growthDateRangeAvailable"
    >
      <span class="arrow flex items-center">
        <img
          class="w-5 h-5 block"
          src="/assets/shared-icons/neptune-icon-blue-solid-chevron-down.svg"
          alt="growth-arrow-icon"
          [ngClass]="percentGrowth >= 0 ? 'rotate-180' : ''"
        />
      </span>
      <span class="growth-value text-neptune-color-primary-500 font-medium">
        {{ this.percentGrowth | signedNumber }}%
      </span>
    </div>
  </section>
</ng-template>

<!-- Skeleton dispatcher: picks the right skeleton based on division -->
<ng-template #metricCardSkeletonLoader>
  @if (division === Division.Pest) {
    <ng-container *ngTemplateOutlet="pestMetricCardSkeleton"></ng-container>
  } @else {
    <ng-container *ngTemplateOutlet="nonPestMetricCardSkeleton"></ng-container>
  }
</ng-template>

<!-- Non-Pest skeleton: single metric value row + avg + View Details footer -->
<ng-template #nonPestMetricCardSkeleton>
  <div
    id="metricCardSkeletonLoader"
    class="h-full w-full box-border p-4 rounded-lg border-solid border-1 border-sky-blue animate-pulse"
  >
    <div class="flex flex-col gap-4 h-full justify-between">
      <section class="flex flex-col gap-2">
        <section class="flex flex-col gap-1">
          <!-- Title + checkbox -->
          <section class="flex flex-col lg:flex-row justify-between items-start">
            <div class="h-5 w-40 bg-neptune-subtle rounded"></div>
            <div class="flex gap-2 items-center">
              <div class="h-4 w-4 bg-neptune-subtle rounded"></div>
              <div class="h-4 w-8 bg-neptune-subtle rounded"></div>
            </div>
          </section>

          <!-- Metric value + growth indicator -->
          <section class="flex gap-2 items-center">
            <div class="h-10 w-24 bg-neptune-subtle rounded"></div>
            <div class="h-6 w-16 bg-neptune-subtle rounded"></div>
          </section>
        </section>

        <!-- Average per location -->
        <section>
          <div class="h-4 w-32 bg-neptune-subtle rounded"></div>
        </section>
      </section>

      <!-- View Details footer -->
      <section>
        <div class="h-10 w-full bg-neptune-subtle rounded-lg"></div>
      </section>
    </div>
  </div>
</ng-template>

<!-- Pest skeleton: two-column metric row (period+count | YoY+growth), no footer -->
<ng-template #pestMetricCardSkeleton>
  <div
    id="pestMetricCardSkeletonLoader"
    class="h-full w-full box-border p-4 rounded-lg border-solid border-1 border-sky-blue animate-pulse"
  >
    <div class="flex flex-col gap-4 h-full">
      <section class="flex flex-col gap-2">
        <section class="flex flex-col gap-1">
          <!-- Title + checkbox -->
          <section class="flex flex-col lg:flex-row justify-between items-start">
            <div class="h-5 w-40 bg-neptune-subtle rounded"></div>
            <div class="flex gap-2 items-center">
              <div class="h-4 w-4 bg-neptune-subtle rounded"></div>
              <div class="h-4 w-8 bg-neptune-subtle rounded"></div>
            </div>
          </section>

          <!-- Two-column metric value: mirrors #pestServiceMetricBlock -->
          <section class="flex gap-2 items-center justify-between">
            <!-- Left: period label + count -->
            <div class="flex flex-col gap-2">
              <div class="h-3 w-20 bg-neptune-subtle rounded"></div>
              <div class="h-9 w-24 bg-neptune-subtle rounded"></div>
            </div>
            <!-- Right: YoY Change label + growth value -->
            <div class="flex flex-col gap-2 items-end">
              <div class="h-3 w-16 bg-neptune-subtle rounded"></div>
              <div class="h-6 w-14 bg-neptune-subtle rounded"></div>
            </div>
          </section>
        </section>

        <!-- Average per location -->
        <section>
          <div class="h-4 w-32 bg-neptune-subtle rounded"></div>
        </section>
      </section>
    </div>
  </div>
</ng-template>
